"use client";

import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * The one Web-Share-with-clipboard-fallback button.
 *
 * `ShareOrderButton`, `ShareProductButton` and `ShareEventButton` had three
 * copies of the same `handleShare` — feature-detect `navigator.share`, share,
 * treat `AbortError` as "the visitor changed their mind", fall back to the
 * clipboard on any other failure, and fall straight to the clipboard when the
 * API is absent. Only the copy strings and the button's children differed.
 *
 * ── WHAT IS AND IS NOT PARAMETERISED ─────────────────────────────────────────
 * The props are all per-call-site COPY (the strings a visitor sees) plus the
 * `Button` pass-through. There is deliberately no `pinterest`/`iconOnly`/
 * `label` prop: the caller renders its own `children`, so an icon-only button, an
 * icon+label button and a button sitting next to a Pinterest link are all just
 * different children in different wrappers — no boolean needed.
 *
 * `sharedMessage` is optional and its ABSENCE is meaningful: the order/product
 * buttons toast "Shared!" on a successful native share, the event button
 * deliberately does not (it sits in a page header where a toast would be noise).
 * Making it optional rather than always-on is what let the event button join.
 *
 * ── WHY THE `Button` PROP ORDER BELOW MUST NOT BE TOUCHED ────────────────────
 * `components/ui/button.tsx` renders `<Comp data-slot … className={cn(…)}
 * {...props} />`, so whatever is left in `...props` reaches the DOM in the order
 * the keys were written here. Reordering `type` and `aria-label` would reorder
 * the rendered attributes — invisible to a browser, but a real change to the
 * emitted HTML, and `tests/unit/components/share/share-primitive-dom-baseline.spec.tsx`
 * compares that HTML byte-for-byte.
 */

/** The `variant` values the share buttons expose. Matches `buttonVariants`. */
export type ShareButtonVariant =
  | "default"
  | "outline"
  | "ghost"
  | "link"
  | "secondary"
  | "destructive";

/** The `size` values the share buttons expose. Matches `buttonVariants`. */
export type ShareButtonSize = "default" | "sm" | "lg" | "icon";

/**
 * Absolute origin for a shareable link.
 *
 * A shared link leaves the site, so it can never be relative. In the browser the
 * live origin is authoritative (it keeps a preview deployment sharing its own
 * host); during SSR there is no `window`, so the build-time public site URL is
 * the only truth available.
 */
export function shareOrigin(): string {
  return typeof window !== "undefined"
    ? window.location.origin
    : process.env.NEXT_PUBLIC_SITE_URL ?? "https://thefloralmuse.uk";
}

interface ShareActionButtonProps {
  /** `navigator.share` title. */
  title: string;
  /** `navigator.share` text. */
  text: string;
  /** Absolute URL — shared, and copied when `clipboardText` includes it. */
  url: string;
  /** What lands on the clipboard when Web Share is unavailable or fails. */
  clipboardText: string;
  /** Toast shown after a successful clipboard copy. */
  copiedMessage: string;
  /** Toast shown when the clipboard write is blocked. */
  failedMessage?: string;
  /** Toast shown after a successful native share. Omit for no toast. */
  sharedMessage?: string;
  /** Accessible name — the button's children may be an icon alone. */
  ariaLabel: string;
  className?: string;
  variant?: ShareButtonVariant;
  size?: ShareButtonSize;
  children?: ReactNode;
}

export function ShareActionButton({
  title,
  text,
  url,
  clipboardText,
  copiedMessage,
  failedMessage = "Failed to copy",
  sharedMessage,
  ariaLabel,
  className,
  variant,
  size,
  children,
}: ShareActionButtonProps) {
  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(clipboardText);
      toast.success(copiedMessage);
    } catch {
      toast.error(failedMessage);
    }
  }

  async function handleShare() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url,
        });
        if (sharedMessage) toast.success(sharedMessage);
        return;
      } catch (err) {
        // An AbortError is the visitor dismissing the share sheet — that is a
        // completed interaction, not a failure, so it must NOT fall through to
        // the clipboard (silently copying after a cancel is a surprise).
        if ((err as Error).name === "AbortError") return;
      }
    }
    await copyToClipboard();
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      onClick={handleShare}
      aria-label={ariaLabel}
    >
      {children}
    </Button>
  );
}
